import {
  getBonusTransactionRequest,
  getSingleBonusRequest,
  makeFloat,
  useCurrency,
  useGetShortBalance
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';

export const useBonusModalData = () => {
  const { refetch } = useGetShortBalance({ enabled: false });
  const { currencyName, currencyIcon } = useCurrency();
  const { t } = useTranslation();

  const getBonusInfo = async (bonusId: number) => {
    if (!bonusId) {
      return;
    }

    try {
      const [{ data }, bonusTransaction] = await Promise.all([
        refetch(),
        getBonusTransactionRequest({
          transaction_id: bonusId
        })
      ]);

      const hasActiveBonus = Number(data?.data?.bonusCasino) > 0;

      if (!bonusTransaction?.data.bonus?.id) {
        const {
          amount: transactionAmount,
          wager: transactionWager,
          maxBet: transactionMaxBet
        } = bonusTransaction?.data || {};

        return {
          amount: transactionAmount ? `${currencyIcon}${makeFloat(transactionAmount)}` : undefined,
          wager: transactionWager ? `x${transactionWager}` : undefined,
          maxBet: transactionMaxBet ? `${currencyIcon}${makeFloat(transactionMaxBet)}` : undefined,
          hasActiveBonus,
          bonusId: bonusTransaction.data.id,
          transactionType: bonusTransaction.data.type
        };
      }

      let bonus = null;

      if (bonusTransaction.data?.bonus?.id) {
        try {
          bonus = await getSingleBonusRequest({
            bonus_id: String(bonusTransaction?.data?.bonus.id)
          });
        } catch (error) {
          console.warn('Failed to fetch bonus:', error);
        }
      }

      if (bonus?.data) {
        const {
          amount: bonusAmounts,
          freeSpins,
          points,
          freeSpinsBetAmount,
          maxBet: maxBetAmounts,
          wager,
          name
        } = bonus.data;

        const formatCurrency = (value: number | undefined) =>
          value ? `${currencyIcon}${makeFloat(value)}` : undefined;

        const bonusParts = [
          formatCurrency(bonusAmounts?.[currencyName as never]),
          freeSpins ? `${freeSpins} FS` : undefined,
          points ? `${points} ${t('bonusModal.points')}` : undefined
        ].filter(Boolean);

        return {
          amount: bonusParts.length > 0 ? bonusParts.join(' + ') : undefined,
          freeSpinBet: formatCurrency(freeSpinsBetAmount?.[currencyName]),
          maxBet: formatCurrency(maxBetAmounts?.[currencyName]),
          wager: wager ? `x${wager}` : undefined,
          freeSpins,
          name,
          bonusId,
          hasActiveBonus
        };
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    }
  };

  return getBonusInfo;
};
